/**
 * Photo service for managing photo operations
 * Handles photo fetching, uploading, deletion, and album management
 * Integrates with backend API and S3 for pre-signed URLs
 *
 * @module photoService
 */

import { logger } from '@/utils/logger';
import { API_BASE_URL, API_ENDPOINTS, PHOTO_CONFIG } from '@/utils/constants';
import { getSession } from './authService';
import type {
  Photo,
  Album,
  UploadProgress,
  ApiResponse,
  PaginatedResponse,
} from '@/types';

// TODO: Add PresignedUrlResponse type when standardizing API responses

// ============================================================================
// AUTH HELPER
// ============================================================================

/**
 * Get authorization headers for API requests
 * @returns Headers object with Authorization bearer token
 * @throws Error if no session available
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await getSession();

  if (!session?.idToken) {
    logger.error(`[WALRUS] photoService: no auth session available`);
    throw new Error('Authentication required');
  }

  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.idToken}`,
  };
}

// ============================================================================
// PHOTO ENDPOINTS
// ============================================================================

/**
 * Get paginated list of photos
 * Supports filtering and pagination
 *
 * @param albumId Optional album ID to filter photos
 * @param page Page number (0-indexed)
 * @param pageSize Number of photos per page
 * @returns Promise resolving to paginated photo list
 */
export async function getPhotos(
  albumId?: string,
  page: number = 0,
  pageSize: number = 24
): Promise<PaginatedResponse<Photo>> {
  logger.debug(`[WALRUS] photoService.getPhotos: fetching photos`, {
    albumId,
    page,
    pageSize,
  });

  try {
    const params = new URLSearchParams();
    if (albumId) params.append('albumId', albumId);
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));

    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.PHOTOS_LIST}?${params.toString()}`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      logger.error(`[WALRUS] photoService.getPhotos: failed with status ${response.status}`);
      throw new Error(`Failed to fetch photos: ${response.statusText}`);
    }

    const data: ApiResponse<PaginatedResponse<Photo>> = await response.json();

    if (!data.success || !data.data) {
      logger.error(`[WALRUS] photoService.getPhotos: API returned error`, { error: data.error });
      throw new Error(data.error || 'Failed to fetch photos');
    }

    logger.debug(`[WALRUS] photoService.getPhotos: fetched ${data.data.items.length} photos`, {
      totalCount: data.data.totalCount,
      hasNextPage: !!data.data.nextToken,
    });

    return data.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`[WALRUS] photoService.getPhotos: error`, { error: errorMessage });
    throw error;
  }
}

/**
 * Get single photo details
 *
 * @param id Photo ID
 * @returns Promise resolving to photo details
 */
export async function getPhoto(id: string): Promise<Photo> {
  logger.debug(`[WALRUS] photoService.getPhoto: fetching photo ${id}`);

  try {
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.PHOTOS_DETAILS.replace(':id', id)}`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      logger.error(`[WALRUS] photoService.getPhoto: failed with status ${response.status}`);
      throw new Error(`Failed to fetch photo: ${response.statusText}`);
    }

    const data: ApiResponse<Photo> = await response.json();

    if (!data.success || !data.data) {
      logger.error(`[WALRUS] photoService.getPhoto: API returned error`, { error: data.error });
      throw new Error(data.error || 'Failed to fetch photo');
    }

    logger.debug(`[WALRUS] photoService.getPhoto: fetched photo ${id}`);
    return data.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`[WALRUS] photoService.getPhoto: error`, { error: errorMessage });
    throw error;
  }
}

/**
 * Upload a photo to S3 using pre-signed URL
 * Validates file type and size before uploading
 *
 * @param file File to upload
 * @param albumId Optional album ID to associate with photo
 * @param onProgress Optional callback for upload progress
 * @returns Promise resolving to uploaded photo
 */
export async function uploadPhoto(
  file: File,
  albumId?: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<Photo> {
  logger.debug(`[WALRUS] photoService.uploadPhoto: starting upload for ${file.name}`, {
    size: file.size,
    type: file.type,
    albumId,
  });

  // Validate file type
  if (!PHOTO_CONFIG.allowedTypes.includes(file.type as any)) {
    logger.warn(`[WALRUS] photoService.uploadPhoto: invalid file type ${file.type}`);
    throw new Error(
      `Invalid file type. Allowed types: ${PHOTO_CONFIG.allowedTypes.join(', ')}`
    );
  }

  // Validate file size
  if (file.size > PHOTO_CONFIG.maxFileSize) {
    logger.warn(`[WALRUS] photoService.uploadPhoto: file size ${file.size} exceeds limit`);
    throw new Error(
      `File size exceeds maximum of ${PHOTO_CONFIG.maxFileSize / 1024 / 1024}MB`
    );
  }

  try {
    // Step 1: Get pre-signed URL from backend
    logger.debug(`[WALRUS] photoService.uploadPhoto: requesting pre-signed URL`);

    const headers = await getAuthHeaders();

    const presignResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PHOTOS_PRESIGNED_URL}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'upload',
        filename: file.name,
        contentType: file.type,
      }),
    });

    if (!presignResponse.ok) {
      logger.error(
        `[WALRUS] photoService.uploadPhoto: pre-signed URL request failed with status ${presignResponse.status}`
      );
      throw new Error(`Failed to get upload URL: ${presignResponse.statusText}`);
    }

    const presignData = await presignResponse.json();

    // Handle both wrapped ApiResponse and direct response formats
    const presignedUrl = presignData.presignedUrl || presignData.data?.url;
    const s3Key = presignData.s3Key || presignData.data?.key;

    if (!presignedUrl || !s3Key) {
      logger.error(`[WALRUS] photoService.uploadPhoto: API returned invalid response`, {
        presignData,
      });
      throw new Error('Failed to get upload URL');
    }

    logger.debug(`[WALRUS] photoService.uploadPhoto: received pre-signed URL`, { key: s3Key });

    // Step 2: Upload file to S3 using pre-signed URL
    logger.debug(`[WALRUS] photoService.uploadPhoto: uploading to S3`);

    if (onProgress) {
      onProgress({
        filename: file.name,
        progress: 10,
        status: 'uploading',
      });
    }

    const s3Response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'x-amz-server-side-encryption': 'AES256',
      },
      body: file,
    });

    if (!s3Response.ok) {
      logger.error(
        `[WALRUS] photoService.uploadPhoto: S3 upload failed with status ${s3Response.status}`
      );
      throw new Error(`S3 upload failed: ${s3Response.statusText}`);
    }

    logger.debug(`[WALRUS] photoService.uploadPhoto: S3 upload successful`);

    if (onProgress) {
      onProgress({
        filename: file.name,
        progress: 100,
        status: 'success',
      });
    }

    // Step 3: Create photo record in database
    logger.debug(`[WALRUS] photoService.uploadPhoto: creating photo record in database`);

    const createResponse = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PHOTOS_LIST}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        title: file.name.replace(/\.[^/.]+$/, ''),  // Use filename without extension as title
        s3Key,
        filename: file.name,
        contentType: file.type,
        size: file.size,
        albumId,
      }),
    });

    if (!createResponse.ok) {
      logger.error(
        `[WALRUS] photoService.uploadPhoto: photo record creation failed with status ${createResponse.status}`
      );
      throw new Error(`Failed to create photo record: ${createResponse.statusText}`);
    }

    const createData: ApiResponse<Photo> = await createResponse.json();

    if (!createData.success || !createData.data) {
      logger.error(`[WALRUS] photoService.uploadPhoto: API returned error`, {
        error: createData.error,
      });
      throw new Error(createData.error || 'Failed to create photo record');
    }

    logger.info(`[WALRUS] photoService.uploadPhoto: photo uploaded successfully`, {
      photoId: createData.data.id,
      filename: file.name,
    });

    return createData.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`[WALRUS] photoService.uploadPhoto: error`, { error: errorMessage });

    if (onProgress) {
      onProgress({
        filename: file.name,
        progress: 0,
        status: 'error',
        error: errorMessage,
      });
    }

    throw error;
  }
}

/**
 * Delete a photo
 * Only owner or admin can delete
 *
 * @param id Photo ID to delete
 * @returns Promise resolving when photo is deleted
 */
export async function deletePhoto(id: string): Promise<void> {
  logger.debug(`[WALRUS] photoService.deletePhoto: deleting photo ${id}`);

  try {
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.PHOTOS_DELETE.replace(':id', id)}`,
      {
        method: 'DELETE',
        headers,
      }
    );

    if (!response.ok) {
      logger.error(`[WALRUS] photoService.deletePhoto: failed with status ${response.status}`);
      throw new Error(`Failed to delete photo: ${response.statusText}`);
    }

    const data: ApiResponse = await response.json();

    if (!data.success) {
      logger.error(`[WALRUS] photoService.deletePhoto: API returned error`, { error: data.error });
      throw new Error(data.error || 'Failed to delete photo');
    }

    logger.info(`[WALRUS] photoService.deletePhoto: photo deleted successfully`, { id });
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`[WALRUS] photoService.deletePhoto: error`, { error: errorMessage });
    throw error;
  }
}

/**
 * Move a photo to a different album (or remove from album)
 * Only owner or admin can move photos
 *
 * @param photoId Photo ID to move
 * @param albumId Target album ID, or null to remove from any album
 * @returns Promise resolving to updated photo
 */
export async function movePhoto(photoId: string, albumId: string | null): Promise<Photo> {
  logger.debug(`[WALRUS] photoService.movePhoto: moving photo ${photoId}`, {
    targetAlbumId: albumId,
  });

  try {
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${API_BASE_URL}${API_ENDPOINTS.PHOTOS_UPDATE.replace(':id', photoId)}`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify({ albumId }),
      }
    );

    if (!response.ok) {
      logger.error(`[WALRUS] photoService.movePhoto: failed with status ${response.status}`);
      throw new Error(`Failed to move photo: ${response.statusText}`);
    }

    const data: ApiResponse<Photo> = await response.json();

    if (!data.success || !data.data) {
      logger.error(`[WALRUS] photoService.movePhoto: API returned error`, { error: data.error });
      throw new Error(data.error || 'Failed to move photo');
    }

    logger.info(`[WALRUS] photoService.movePhoto: photo moved successfully`, {
      photoId,
      targetAlbumId: albumId,
    });

    return data.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`[WALRUS] photoService.movePhoto: error`, { error: errorMessage });
    throw error;
  }
}

// ============================================================================
// ALBUM ENDPOINTS
// ============================================================================

/**
 * Get list of albums
 *
 * @returns Promise resolving to album list
 */
export async function getAlbums(): Promise<Album[]> {
  logger.debug(`[WALRUS] photoService.getAlbums: fetching albums`);

  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ALBUMS_LIST}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      logger.error(`[WALRUS] photoService.getAlbums: failed with status ${response.status}`);
      throw new Error(`Failed to fetch albums: ${response.statusText}`);
    }

    const data: ApiResponse<Album[]> = await response.json();

    if (!data.success || !data.data) {
      logger.error(`[WALRUS] photoService.getAlbums: API returned error`, { error: data.error });
      throw new Error(data.error || 'Failed to fetch albums');
    }

    logger.debug(`[WALRUS] photoService.getAlbums: fetched ${data.data.length} albums`);
    return data.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`[WALRUS] photoService.getAlbums: error`, { error: errorMessage });
    throw error;
  }
}

/**
 * Create a new album (any authenticated user)
 *
 * @param name Album name
 * @param description Optional album description
 * @returns Promise resolving to created album
 */
export async function createAlbum(name: string, description?: string): Promise<Album> {
  logger.debug(`[WALRUS] photoService.createAlbum: creating album ${name}`, { description });

  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.ALBUMS_CREATE}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name,
        description,
      }),
    });

    if (!response.ok) {
      logger.error(`[WALRUS] photoService.createAlbum: failed with status ${response.status}`);
      throw new Error(`Failed to create album: ${response.statusText}`);
    }

    const data: ApiResponse<Album> = await response.json();

    if (!data.success || !data.data) {
      logger.error(`[WALRUS] photoService.createAlbum: API returned error`, { error: data.error });
      throw new Error(data.error || 'Failed to create album');
    }

    logger.info(`[WALRUS] photoService.createAlbum: album created successfully`, {
      albumId: data.data.id,
      name,
    });

    return data.data;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`[WALRUS] photoService.createAlbum: error`, { error: errorMessage });
    throw error;
  }
}

// ============================================================================
// PRE-SIGNED URL ENDPOINTS
// ============================================================================

/**
 * Get pre-signed URL for downloading a photo
 *
 * @param key S3 object key
 * @returns Promise resolving to pre-signed download URL
 */
export async function getDownloadUrl(key: string): Promise<string> {
  logger.debug(`[WALRUS] photoService.getDownloadUrl: getting download URL for key ${key}`);

  try {
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PHOTOS_PRESIGNED_URL}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        action: 'download',
        key,
      }),
    });

    if (!response.ok) {
      logger.error(
        `[WALRUS] photoService.getDownloadUrl: failed with status ${response.status}`
      );
      throw new Error(`Failed to get download URL: ${response.statusText}`);
    }

    const data = await response.json();

    // Handle both response formats
    const presignedUrl = data.presignedUrl || data.data?.url;

    if (!presignedUrl) {
      logger.error(`[WALRUS] photoService.getDownloadUrl: API returned invalid response`, {
        data,
      });
      throw new Error('Failed to get download URL');
    }

    logger.debug(`[WALRUS] photoService.getDownloadUrl: got download URL`, { key });
    return presignedUrl;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error(`[WALRUS] photoService.getDownloadUrl: error`, { error: errorMessage });
    throw error;
  }
}

// TODO: Add photo search functionality
// TODO: Add photo tagging support
// TODO: Add photo editing capabilities (caption, tags)
// TODO: Add batch operations (delete multiple, move to album)
// TODO: Add photo favorites/rating system
